import React, { useEffect, useState, Suspense } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import './app.scss';
import { UserRoutes } from './routes/userRoute/UserRoute';

// Lazy-load admin routes to reduce initial bundle size for regular visitors
const AdminRoutes = React.lazy(() => import('./routes/adminRoute/AdminRoutes').then(m => ({ default: m.AdminRoutes })));
import { IRootState, IAppDispatch } from './redux/store';
import { useSelector, useDispatch } from 'react-redux';
import { ConfigProvider, Spin } from 'antd';
import Cookies from 'js-cookie';
import { getUserInfo } from './redux/api/authApi';
import { fetchCategories } from './redux/categories/categoriesSlice';
import WhatsAppContact from './components/WhatsAppContact';

function App() {
    const dispatch = useDispatch<IAppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const { role, userData, loading } = useSelector((state: IRootState) => state.auth);
    const [isInitializing, setIsInitializing] = useState(true);

    // Session restoration on app initialization
    useEffect(() => {
        const restoreSession = async () => {
            const token = Cookies.get('accessToken');
            const protectedRoutes = ['/profile', '/checkout', '/cart'];
            const isAdminRoute = location.pathname.startsWith('/admin');
            const isProtected = protectedRoutes.some(route => location.pathname.startsWith(route)) || isAdminRoute;

            if (token) {
                if (isProtected) {
                    try {
                        // Priority lock: Validate token before rendering protected views
                        await dispatch(getUserInfo()).unwrap();
                    } catch (error) {
                        console.error('Session restoration failed:', error);
                        Cookies.remove('accessToken');
                        if (isAdminRoute && location.pathname !== '/admin/signIn') {
                            navigate('/admin/signIn');
                        } else {
                            navigate(`/signIn?redirect=${location.pathname.slice(1)}`);
                        }
                    }
                } else {
                    // Non-protected route (e.g. Homepage): Do not lock initial render! Fetch in background.
                    dispatch(getUserInfo());
                }
            } else {
                // No token, redirect if on protected route
                if (isProtected) {
                    if (isAdminRoute && location.pathname !== '/admin/signIn') {
                        navigate('/admin/signIn');
                    } else {
                        navigate(`/signIn?redirect=${location.pathname.slice(1)}`);
                    }
                }
            }

            setIsInitializing(false);
        };

        restoreSession();
        dispatch(fetchCategories());
    }, []); // Run only once on mount

    // Show loading spinner during initialization
    if (isInitializing) {
        return (
            <ConfigProvider
                theme={{
                    token: {
                        colorPrimary: '#A67C52',
                        fontFamily: "'Inter', sans-serif",
                    },
                }}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}>
                    <Spin size="large" />
                </div>
            </ConfigProvider>
        );
    }

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: '#A67C52',
                    fontFamily: "'Inter', sans-serif",
                },
            }}
        >
            <Routes>
                <Route path="/*" element={<UserRoutes />} />
                <Route path="/admin/*" element={
                    <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Spin size="large" /></div>}>
                        <AdminRoutes />
                    </Suspense>
                } />
            </Routes>
            <WhatsAppContact />
        </ConfigProvider>
    );
}

export default App;
